import { SETTINGS_ROUTES } from "../settingProfile/settingsRoutes";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../layout/Layout.css";
import "../homeDashboard/HomeDashboardSidebar.css";
import "./HomeDashboardHeader.css";

import homebgImg from "../assets/images/homebgImg.png";
import ExamCategorySlider from "./ExamCategorySlider";
import CurrentlyStudyingSlider from "./CurrentlyStudyingSlider";
import { useNotifications } from "../hooks/useNotifications";
import useProfileSettingsProfile from "../hooks/useProfileSettingsProfile";
import usePreferredLanguageToggle from "../hooks/usePreferredLanguageToggle";
import { SEARCH_ROUTES } from './search/searchRoutes';
import { navigateSearch } from './search/searchFlow';

const detailExam = [
  "12+ Exams",
  "8,500+ Courses",
  "1,25,000+ Students",
];

const Header = ({ toggleSidebar, isSidebarOpen, sidebarData, dashboardHeaderData }) => {
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState('');
  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const keyword = searchKeyword.trim();
    if (!keyword) return;
    event.currentTarget.querySelector('input')?.blur();
    navigateSearch(navigate, SEARCH_ROUTES.categories, {
      entryKeyword: keyword,
      entrySearchId: Date.now(),
    });
  };
  const { data: profileData } = useProfileSettingsProfile();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isBackgroundImageLoaded, setIsBackgroundImageLoaded] = useState(false);
  const [resolvedBackgroundImage, setResolvedBackgroundImage] = useState("");
  const { data: unreadNotificationsData } = useNotifications({ page: 1, unreadOnly: true });

  const effectiveProfile = profileData || sidebarData?.user_data || {};
  const {
    currentPreferredLanguage,
    handleLanguageToggle,
    isPending: isLanguageUpdatePending,
  } = usePreferredLanguageToggle(effectiveProfile || sidebarData?.user_data);

  const profile = sidebarData?.user_data || {};
  const stateImage = dashboardHeaderData?.exam_category_detail?.state_image || "";

  const unreadNotificationCount = useMemo(
    () => Number(unreadNotificationsData?.pagination?.totalRecords || 0),
    [unreadNotificationsData]
  );

  const unreadNotificationLabel = unreadNotificationCount > 99 ? "99+" : String(unreadNotificationCount);

  const currentDateLabel = useMemo(
    () =>
      new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date()),
    []
  );

  const currentStateLabel =
    profile?.state_name ||
    profile?.state ||
    profile?.user_state_name ||
    sidebarData?.selected_state?.state_name ||
    "State not selected";

  useEffect(() => {
    let isMounted = true;

    setIsBackgroundImageLoaded(false);
    setResolvedBackgroundImage("");

    if (!stateImage) {
      setResolvedBackgroundImage(homebgImg);
      setIsBackgroundImageLoaded(true);
      return undefined;
    }

    const imageLoader = new Image();
    imageLoader.onload = () => {
      if (!isMounted) {
        return;
      }

      setResolvedBackgroundImage(stateImage);
      setIsBackgroundImageLoaded(true);
    };
    imageLoader.onerror = () => {
      if (!isMounted) {
        return;
      }

      setResolvedBackgroundImage(homebgImg);
      setIsBackgroundImageLoaded(true);
    };
    imageLoader.src = stateImage;

    return () => {
      isMounted = false;
    };
  }, [stateImage]);

  return (
    <header
      className={`headerDash homedashboard-header ${
        isSidebarOpen ? "shifted" : ""
      }`}
    >
      <div className="dash-main-heading hd-header-shell">
        <div className="hd-panel">
          {resolvedBackgroundImage ? (
            <div
              className="hd-panel__bg"
              aria-hidden="true"
              style={{ backgroundImage: `url(${resolvedBackgroundImage})` }}
            />
          ) : null}

          {!isBackgroundImageLoaded ? (
            <div className="hd-panel__shimmer" aria-hidden="true" />
          ) : null}

          <div className="hd-panel__overlay" aria-hidden="true" />

          <div className="hd-panel__content">
            <div className="hd-topbar">
              <div className="hd-topbar__left">
                <div className="hd-menuButton" onClick={toggleSidebar}>
                  <i className="fa-solid fa-bars"></i>
                </div>

                <form className="hd-search" role="search" onSubmit={handleSearchSubmit}>
                  <span className="hd-search__refresh" aria-hidden="true">
                    <i className="bi bi-arrow-clockwise" />
                  </span>

                  <input
                    type="text"
                    className="hd-search__input"
                    placeholder="Search for courses, tests, notes, PYQ..."
                    aria-label="Search for courses, tests, notes, PYQ"
                    value={searchKeyword}
                    onChange={(event) => setSearchKeyword(event.target.value)}
                  />

                  <button
                    className="hd-search__button"
                    type="submit"
                    aria-label="Search"
                  >
                    <i className="bi bi-search" />
                  </button>
                </form>
              </div>

              <div className="hd-topmeta">
                <span className="hd-topmeta__item">
                  <i className="bi bi-calendar2-week" />
                  <span>{currentDateLabel}</span>
                </span>

                <span className="hd-topmeta__item">
                  <i className="bi bi-geo-alt" />
                  <span>{currentStateLabel}</span>
                </span>

                <button
                  className={`hd-iconButton hd-iconButton--language${currentPreferredLanguage === "HI" ? " is-hi" : ""}${isLanguageUpdatePending ? " is-loading" : ""}`}
                  type="button"
                  aria-label={`Switch language to ${currentPreferredLanguage === "HI" ? "English" : "Hindi"}`}
                  title={`Current language: ${currentPreferredLanguage}. Click to switch to ${currentPreferredLanguage === "HI" ? "English" : "Hindi"}`}
                  onClick={handleLanguageToggle}
                  disabled={isLanguageUpdatePending}
                >
                  <i className="bi bi-globe" />
                  <span className="hd-lang-badge">{currentPreferredLanguage}</span>
                </button>

                <button
                  className="hd-notification"
                  type="button"
                  aria-label="Notifications"
                  onClick={() => navigate(SETTINGS_ROUTES.notifications)}
                >
                  <i className="bi bi-bell" />
                  {unreadNotificationCount > 0 ? (
                    <span className="hd-notification__badge">{unreadNotificationLabel}</span>
                  ) : null}
                </button>

                <div className="homeuser-profile__avatar">
                  <i className="bi bi-shield-fill-check"></i>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-between">
              <div className="explore-categories">
                <h1 className="hd-hero__title">
                  {selectedCategory?.name || "Explore Exams"}{" "}
                </h1>

                <div className="details-examss">
                  {detailExam.map((item, index) => (
                    <span key={index} className="detail-item">
                      {item}
                    </span>
                  ))}
                </div>

                <div className="explore-exam">
                  <div className="hd-cta">
                    <span>Explore Exam Categories</span>
                  </div>

                  <ExamCategorySlider onActiveCategoryChange={setSelectedCategory} />
                </div>

                <div className="currently-studying">
                  <div className="hd-cta">
                    <span> Currently Studying</span>
                  </div>
                  <CurrentlyStudyingSlider categoryId={selectedCategory?.category_id} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
